// src/utils/calibrationUtils.js

/**
 * Empirical calibration coefficients derived from calibration analysis
 * Quadratic model: correction_factor = a*T² + b*T + c
 */
const EMPIRICAL_COEFFS = {
  a: -0.000026,
  b: 0.002039,
  c: 0.963346,
};

/**
 * Calculate speed of sound based on environmental conditions
 * Matches SensorManager::calculateSpeedOfSound in firmware
 *
 * @param {number} temperatureC - Temperature in Celsius
 * @param {number} humidityPct - Relative humidity percentage
 * @param {number} pressurePa - Atmospheric pressure in Pascals
 * @returns {number} Speed of sound in m/s
 */
export const calculateSpeedOfSound = (
  temperatureC,
  humidityPct,
  pressurePa,
) => {
  let speed = 331.3 * Math.sqrt(1 + temperatureC / 273.15);
  speed = speed + 0.0124 * humidityPct;

  const pressureCorrection = ((pressurePa - 101325.0) / 101325.0) * 0.5;
  speed = speed * (1 + pressureCorrection);

  return speed;
};

/**
 * Calculate calibrated distance from ultrasonic duration using physics model
 *
 * @param {number} durationUs - Ultrasonic round-trip time in microseconds
 * @param {number} temperatureC - Temperature in Celsius
 * @param {number} humidityPct - Relative humidity percentage
 * @param {number} pressurePa - Atmospheric pressure in Pascals
 * @returns {number} Calibrated distance in cm
 */
export const calibratedDistanceFromDuration = (
  durationUs,
  temperatureC,
  humidityPct,
  pressurePa,
) => {
  const speedMs = calculateSpeedOfSound(temperatureC, humidityPct, pressurePa);
  const speedCmUs = (speedMs * 100.0) / 1_000_000.0;

  return (durationUs * speedCmUs) / 2.0;
};

/**
 * Estimate ultrasonic duration from raw distance measurement
 * Assumes default speed of sound at 20°C (343 m/s = 0.0343 cm/μs)
 *
 * @param {number} distanceCm - Raw distance in cm
 * @returns {number} Estimated duration in microseconds
 */
export const estimateDuration = (distanceCm) => {
  const DEFAULT_SPEED_CM_US = 0.0343;
  return (2.0 * distanceCm) / DEFAULT_SPEED_CM_US;
};

/**
 * Calculate empirical correction factor from temperature
 * Uses quadratic model to account for sensor drift and thermal effects
 *
 * @param {number} temperatureC - Temperature in Celsius
 * @returns {number} Multiplicative correction factor
 */
export const getEmpiricalCorrectionFactor = (temperatureC) => {
  const { a, b, c } = EMPIRICAL_COEFFS;
  return a * temperatureC * temperatureC + b * temperatureC + c;
};

/**
 * Apply empirical correction to physics-calibrated distance
 * This corrects for residual temperature drift not captured by physics model
 *
 * @param {number} distanceCalibrated - Physics-calibrated distance in cm
 * @param {number} temperatureC - Temperature in Celsius
 * @returns {number|null} Empirically corrected distance in cm, or null if input invalid
 */
export const applyEmpiricalCalibration = (distanceCalibrated, temperatureC) => {
  if (!distanceCalibrated || !temperatureC) return null;

  const correctionFactor = getEmpiricalCorrectionFactor(temperatureC);
  return distanceCalibrated * correctionFactor;
};

/**
 * Calculate water level from distance measurement
 * Water Level = Offset - Distance
 *
 * @param {number} distanceCm - Distance from sensor to water surface in cm
 * @param {number} offsetCm - Distance from sensor to ground/bottom in cm
 * @returns {number} Water level in cm (negative = below ground; 0 only for an invalid reading or offset)
 */
export const calculateWaterLevel = (distanceCm, offsetCm) => {
  if (!distanceCm || distanceCm <= 0 || !offsetCm) return 0;

  const waterLevel = offsetCm - distanceCm;
  return Math.round(waterLevel * 10) / 10; // Round to 1 decimal place
};

/**
 * Reject outlier readings that spike too far from previous valid reading
 *
 * @param {Array} data - Array of distance readings
 * @param {number} thresholdCm - Maximum allowed change between consecutive readings
 * @returns {Array} Cleaned data with outliers set to null
 */
export const rejectOutliers = (data, thresholdCm = 10) => {
  if (!data || data.length === 0) return data;

  const cleaned = [...data];
  let lastValid = cleaned[0];

  for (let i = 1; i < cleaned.length; i++) {
    if (cleaned[i] != null && Math.abs(cleaned[i] - lastValid) > thresholdCm) {
      cleaned[i] = null; // Mark as outlier
    } else if (cleaned[i] != null) {
      lastValid = cleaned[i];
    }
  }

  return cleaned;
};

/**
 * Process a single sensor reading through all calibration stages
 *
 * @param {Object} reading - Raw sensor reading
 * @param {number} reading.distance - Raw distance in cm
 * @param {number} reading.temperature - Temperature in Celsius
 * @param {number} reading.humidity - Humidity percentage
 * @param {number} reading.pressure - Pressure in hPa
 * @returns {Object} Calibrated distances
 */
export const processReading = (reading) => {
  if (!reading) return null;

  const { distance, temperature, humidity, pressure } = reading;

  // Convert pressure from hPa to Pa
  const pressurePa = pressure * 100.0;

  // Estimate duration from raw distance
  const durationUs = estimateDuration(distance);

  // Apply physics-based calibration
  const distanceCalibrated = calibratedDistanceFromDuration(
    durationUs,
    temperature,
    humidity,
    pressurePa,
  );

  // Apply empirical correction
  const distanceEmpirical = applyEmpiricalCalibration(
    distanceCalibrated,
    temperature,
  );

  return {
    raw: distance,
    physicsCalibrated: distanceCalibrated,
    empiricalCalibrated: distanceEmpirical,
  };
};
